import { Head } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form-inertia";
import { Input } from "@/components/ui/input";
import {
  CalendarClock,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Mail,
  Newspaper,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useInertiaForm } from "@/hooks/use-inertia-form";
import { useState } from "react";

const features = [
  {
    icon: Globe,
    title: "Multiblogs centralizados",
    description: "Gerencie todos os blogs WordPress dos seus clientes em um só painel.",
  },
  {
    icon: CalendarClock,
    title: "Agendamento inteligente",
    description: "Programe publicações e deixe a automação trabalhar por você.",
  },
  {
    icon: Sparkles,
    title: "Conteúdo com IA editorial",
    description: "Gere textos guiados por manuais de redação e princípios jornalísticos.",
  },
  {
    icon: ShieldCheck,
    title: "Controle de acessos",
    description: "Papéis e permissões granulares para toda a sua equipe.",
  },
];

export default function AuthLogin() {
  const [showPassword, setShowPassword] = useState(false);

  const { form, handleSubmit } = useInertiaForm({
    url: "/new_session",
    method: "post",
    initialData: {
      email: "",
      password: "",
      remember: false,
    },
  });

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2 bg-background">
      <Head title="Entrar · Memoráveis Network" />

      {/* Painel da marca */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-950 p-12 text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-600/30 blur-3xl" />
          <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-fuchsia-600/20 blur-3xl" />
        </div>

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
            <Newspaper className="h-6 w-6" />
          </div>
          <div className="leading-tight">
            <p className="text-lg font-semibold tracking-tight">Memoráveis Network</p>
            <p className="text-xs text-white/60">Gestão editorial de blogs</p>
          </div>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">
            Produza, agende e publique conteúdo de qualidade em escala.
          </h1>
          <p className="mt-4 text-base text-white/70">
            A plataforma que conecta seus blogs, sua equipe e a inteligência
            artificial — seguindo os princípios dos grandes manuais de redação.
          </p>

          <div className="mt-10 grid gap-5">
            {features.map((feature) => (
              <div key={feature.title} className="flex items-start gap-4">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15">
                  <feature.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">{feature.title}</p>
                  <p className="text-sm text-white/60">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-xs text-white/40">
          © {new Date().getFullYear()} Memoráveis Network. Todos os direitos reservados.
        </p>
      </div>

      {/* Painel do formulário */}
      <div className="flex min-h-screen items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {/* Logo mobile */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Newspaper className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <p className="text-lg font-semibold tracking-tight">Memoráveis Network</p>
              <p className="text-xs text-muted-foreground">Gestão editorial de blogs</p>
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight">Bem-vindo de volta</h2>
            <p className="text-sm text-muted-foreground">
              Entre com suas credenciais para acessar o painel.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <FormField
              name="email"
              form={form}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder="seu@email.com"
                        className="pl-9"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              form={form}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        className="pl-9 pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              size="lg"
              className="w-full"
              isLoading={form.processing}
            >
              {form.processing ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Acesso restrito a usuários autorizados do workspace.
          </p>
        </div>
      </div>
    </div>
  );
}
